import axios from "axios";
import { supabase } from "../lib/supabaseClient";
import { ADMIN_SESSION_KEY, clearAdminImpersonation } from "../lib/adminImpersonation";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Sign in again before opening admin.");

  const adminSession = sessionStorage.getItem(ADMIN_SESSION_KEY) || "";
  return {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      ...(adminSession ? { "X-Admin-Session": adminSession } : {}),
    },
  };
}

function backendError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const detail = data?.detail;
    const msg = data?.error || error.message;
    return new Error(detail ? `${msg}: ${detail}` : msg);
  }
  return error instanceof Error ? error : new Error("Unexpected admin request error.");
}

export function storeAdminSession(token: string) {
  sessionStorage.setItem(ADMIN_SESSION_KEY, token);
}

export function clearAdminSession() {
  sessionStorage.removeItem(ADMIN_SESSION_KEY);
  clearAdminImpersonation();
}

export async function adminStatus(): Promise<{ isPlatformAdmin: boolean; verified: boolean }> {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/session/status`, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function requestAdminOtp(): Promise<{ success: boolean; expiresAt: string; message: string; delivery?: string; devCode?: string }> {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/session/request-otp`, {}, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function verifyAdminOtp(code: string): Promise<{ adminSessionToken: string; expiresAt: string }> {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/session/verify-otp`, { code }, await authHeaders());
    storeAdminSession(response.data.adminSessionToken);
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function revokeAdminSession(): Promise<{ success: boolean }> {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/session/revoke`, {}, await authHeaders());
    clearAdminSession();
    return response.data;
  } catch (error) {
    clearAdminSession();
    throw backendError(error);
  }
}

export async function fetchAdminOverview() {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/overview`, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminOverviewFiltered(params: { businessId?: string; startDate?: string; endDate?: string }) {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/overview`, { ...(await authHeaders()), params });
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminSuppliers(params: { businessId?: string; supplierId?: string }) {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/suppliers`, { ...(await authHeaders()), params });
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminBusinesses() {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/businesses`, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function suspendAdminBusiness(businessId: string, reason: string) {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/businesses/${businessId}/suspend`, { reason }, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function reinstateAdminBusiness(businessId: string) {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/businesses/${businessId}/reinstate`, {}, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function deleteAdminBusiness(businessId: string, confirmName: string) {
  try {
    const response = await axios.delete(`${API_BASE_URL}/admin/businesses/${businessId}`, {
      ...(await authHeaders()),
      data: { confirmName },
    });
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminUsers() {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/users`, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function suspendAdminUser(userId: string, reason: string) {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/users/${userId}/suspend`, { reason }, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function reinstateAdminUser(userId: string) {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/users/${userId}/reinstate`, {}, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function deleteAdminUser(userId: string) {
  try {
    const response = await axios.delete(`${API_BASE_URL}/admin/users/${userId}`, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function startAdminImpersonation(userId: string): Promise<{
  targetUser: { id: string; email?: string; name?: string };
  businesses: { id: string; name: string; role: string }[];
  mode: "read_only";
  startedAt: string;
}> {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/impersonation/start`, { userId }, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function addAdminBusinessMember(businessId: string, payload: { userId?: string; email?: string; role: string }) {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/businesses/${businessId}/members`, payload, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateAdminBusinessMemberRole(businessId: string, userId: string, role: string) {
  try {
    const response = await axios.patch(`${API_BASE_URL}/admin/businesses/${businessId}/members/${userId}`, { role }, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function removeAdminBusinessMember(businessId: string, userId: string) {
  try {
    const response = await axios.delete(`${API_BASE_URL}/admin/businesses/${businessId}/members/${userId}`, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminInvoices(params: { businessId?: string; startDate?: string; endDate?: string } = {}) {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/invoices`, { ...(await authHeaders()), params });
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminIntegrations() {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/integrations`, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function disableAdminIntegration(id: string) {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/integrations/${id}/disable`, {}, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminIssues() {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/issues`, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminAudit(params: { businessId?: string; startDate?: string; endDate?: string } = {}) {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/audit`, { ...(await authHeaders()), params });
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function upsertProductCategoryHint(payload: {
  productCode?: string;
  description: string;
  suggestedCategory: string;
}): Promise<{ id: string; productCode: string | null; descriptionNormalized: string; suggestedCategory: string; updatedAt: string }> {
  try {
    const response = await axios.patch(`${API_BASE_URL}/admin/product-category-hint`, payload, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function updateAdminSupplierCategory(supplierId: string, category: string): Promise<{ id: string; name: string; category: string; business_id: string }> {
  try {
    const response = await axios.patch(`${API_BASE_URL}/admin/suppliers/${supplierId}`, { category }, await authHeaders());
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}

export async function fetchAdminBusinessRegistrations(status?: string) {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/business-registration-requests`, {
      ...(await authHeaders()),
      params: status ? { status } : undefined,
    });
    return response.data as {
      id: string; user_id: string; user_email: string | null;
      name: string; contact_person_name: string | null; address: string | null; industry: string | null;
      abn: string | null; phone: string | null; email: string | null;
      status: "pending" | "approved" | "rejected";
      admin_note: string | null; business_id: string | null; created_at: string;
    }[];
  } catch (error) { throw backendError(error); }
}

export async function approveBusinessRegistration(id: string, adminNote?: string) {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/business-registration-requests/${id}/approve`, { adminNote }, await authHeaders());
    return response.data as { success: boolean; businessId: string };
  } catch (error) { throw backendError(error); }
}

export async function rejectBusinessRegistration(id: string, adminNote?: string) {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/business-registration-requests/${id}/reject`, { adminNote }, await authHeaders());
    return response.data as { success: boolean };
  } catch (error) { throw backendError(error); }
}

export async function deleteProductCategoryHint(id: string): Promise<{ success: boolean; id: string }> {
  try {
    const response = await axios.delete(`${API_BASE_URL}/admin/product-category-hint`, {
      ...(await authHeaders()),
      params: { id },
    });
    return response.data;
  } catch (error) {
    throw backendError(error);
  }
}
